import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useMemo } from 'react';

import { BarChart } from '~/components/BarChart';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

const Y_AXIS_DOMAIN = [0, 0.08];

interface Props {
  predYPercent: number;
  askingYPercent: number;
  askingPrice: number;
  predValue: number;
  data: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['series_c_pricing'];
  className?: string;
}

const Component: React.FC<Props> = ({
  predYPercent,
  askingYPercent,
  askingPrice,
  predValue,
  data,
  className,
}) => {
  const askingY = askingYPercent / 100;
  const predY = predYPercent / 100;

  const barChartData = useMemo(() => {
    return data.bins.map((bin, index) => ({ x: bin, y: data.weights[index] }));
  }, [data.bins, data.weights]);

  const lines = useMemo(() => {
    return [
      { x: askingY, y: Y_AXIS_DOMAIN[1] - 0.01, type: 'success' as const },
      { x: predY, y: Y_AXIS_DOMAIN[1] - 0.01, type: 'error' as const },
      { x: data.mean, y: Y_AXIS_DOMAIN[1] - 0.01, type: 'danger' as const },
    ];
  }, [askingY, data.mean, predY]);

  const xAxisDomain = useMemo(() => {
    return [Math.min(-0.3, askingY, predY), Math.max(0.3, askingY, predY)];
  }, [askingY, predY]);

  return (
    <div className={clsx(styles.container, className)}>
      <Typography color="var(--text-3-color)" mb="14px" variant="subtitle1">
        Asking Valuation: Monthly Grow Rate since Round B
      </Typography>
      <BarChart
        data={barChartData}
        leftBottomLabel={`Asking monthly growth = ${askingYPercent.toFixed(2)}%`}
        leftTopLabel={`Asking price = ${askingPrice.toFixed(0)}`}
        lines={lines}
        rightBottomLabel={`Pred monthly growth = ${predYPercent.toFixed(2)}%`}
        rightTopLabel={`Pred value = ${predValue.toFixed(0)}`}
        xAxisDomain={xAxisDomain}
        xAxisTickCount={7}
        yAxisDomain={Y_AXIS_DOMAIN}
        yAxisTickCount={9}
      />
    </div>
  );
};

export const AskingValuation = memo(Component);
